/* eslint-disable @typescript-eslint/no-explicit-any */
// OneDs: https://1dsdocs.azurewebsites.net/api/webSDK/sku/analytics-web/3.0/f/index.html
import { UserInfo } from '../../web-legacy/models/apiModels';
import { AwaMetaTagType, ContentAttribute } from '../../web-legacy/models/constants/constants';
import { IAppState } from '../../web-legacy/models/reduxState';
import { AuthProvider } from '../models/authProvider';
import { DataBoundary, DataBoundaryValues } from '../models/dataBoundary';
import { LogRequest, LogType } from '../models/emeraldLoggingRequest';

const currentWindow = window as any;

const OneDSEndpoints: { [key: number]: string } = {
  [DataBoundary.ROW]: 'https://browser.events.data.microsoft.com/OneCollector/1.0/',
  [DataBoundary.EU]: 'https://eu-mobile.events.data.microsoft.com/OneCollector/1.0/',
};

const updateOneDsMetadataCache = () => {
  const webAnalytics =
    typeof currentWindow.analytics === 'object' ? currentWindow.analytics.getWebAnalyticsExtension() : null;
  if (!!webAnalytics && !!webAnalytics.refreshMetadata) {
    webAnalytics.refreshMetadata();
  }
};

const userInfoProviderToNameForLog = (provider: string) => {
  switch (provider) {
    case 'WindowsLiveId':
      return 'MSA';
    default:
      return provider;
  }
};

const getIsReInitializationEnabled = () => {
  const metaTagName = 'isOneDSReInitEnabled';
  const metaTag = !!document.getElementsByName(metaTagName) && document.getElementsByName(metaTagName)[0];
  return metaTag?.getAttribute(ContentAttribute) === 'True';
};

const isReInitializationEnabled = getIsReInitializationEnabled();

export const addOrUpdateAwaMetaTag = (awaMetaTagName: AwaMetaTagType, metaTagContent: string) => {
  const contentToBeLogged =
    awaMetaTagName === AwaMetaTagType.AuthType ? userInfoProviderToNameForLog(metaTagContent) : metaTagContent;
  const metaTagElement: HTMLMetaElement = document.head.querySelector(`meta[name="${awaMetaTagName}"]`);
  if (metaTagElement !== null) {
    metaTagElement.content = contentToBeLogged;
  } else {
    const meta: HTMLMetaElement = document.createElement('meta');
    meta.name = awaMetaTagName;
    meta.content = contentToBeLogged;
    document.head.appendChild(meta);
  }

  updateOneDsMetadataCache();
};

export const capturePageView = (overrideValues: any) => {
  const analytics = currentWindow?.analytics;
  if (typeof analytics === 'object') {
    analytics.capturePageView(overrideValues);
  }
};

export const capturePageAction = (element: any, overrideValues: any) => {
  const analytics = currentWindow?.analytics;
  if (typeof analytics === 'object') {
    analytics.capturePageAction(element, overrideValues);
  }
};

export const captureContentUpdate = (overrideValues: any) => {
  const analytics = currentWindow?.analytics;
  if (typeof analytics === 'object') {
    analytics.captureContentUpdate(overrideValues);
  }
};

export const collectTelemetry = (logRequest: LogRequest) => {
  const logHandlerTags = {
    actionType: logRequest.ActionType,
    behavior: logRequest.Behavior,
    content: {
      contentId: logRequest.ContentId,
      formnm: 'MaxFunnel',
      hint: logRequest.Hint,
      details: logRequest.Details,
    },
  };
  switch (logRequest.Type) {
    case LogType.PageView:
      capturePageView(logHandlerTags);
      break;
    case LogType.PageAction:
      capturePageAction(null, logHandlerTags);
      break;
    case LogType.ContentUpdate:
      captureContentUpdate(logHandlerTags);
      break;
    default:
      break;
  }
}

export const logClientDebuggingEvent = (
  behavior: number,
  formName: string,
  contentName: string,
  areaName: string,
  fileName: string,
  functionName: string,
  loggingArea: string,
  message: string
) => {
  const toggleActionOverrideTags = {
    behavior: behavior,
    content: {
      formnm: formName,
      contentName: contentName,
      areaName: areaName,
      fileName: fileName,
      functionName: functionName,
      loggingArea: loggingArea,
      message: message,
    },
  };

  capturePageAction(null, toggleActionOverrideTags);
};

/**
 * This function is used add the error message if any.
 * @param message The error message captured
 */
export const logCustomError = (message: string) => {
  const analytics = currentWindow?.analytics;
  if (analytics) {
    analytics.trackException(
      { exception: { name: 'CustomError', message: message, isManual: true, properties: { errorInfo: message } } },
      {}
    );
  }
};

/**
 * This class is used to hold data that will be sent to 1ds when the re-initiation happened.
 * So the data pipeline can still catch the selfhelp status even all logs before the 1ds 'After'
 * event was missing.
 */
export class SelfHelpStatus {
  public areInsightsPresent: boolean;
  public areSolutionsPresent: boolean;
  public contactSearchedQuery: string;
  public isDeepLinkQuery: boolean;
  public selfhelpprovider: string;
  public alchemyThemeRoutingEnabled: boolean;
  public originalQueryLength: number;
  public isAutoSuggestQuery: boolean;
  public suggestionsCount: number;
  public queryIndex: number;

  public constructor(state: IAppState) {
    const contactState = state.ContactState;
    this.areInsightsPresent = contactState.SelfHelpAlchemyState.areInsightsPresent;
    this.areSolutionsPresent = contactState.SelfHelpAlchemyState.areSolutionsPresent;
    this.contactSearchedQuery = contactState.SharedState.contactSearchedQuery;
    this.isDeepLinkQuery = contactState.SharedState.isDeepLinkQuery;
    this.selfhelpprovider = this.selfhelpprovider ?? 'alchemy';
    this.alchemyThemeRoutingEnabled = contactState.ModalitySectionState.alchemyThemeRoutingEnabled;
    this.originalQueryLength = contactState.AutoSuggestState.originalQuery.length;
    this.isAutoSuggestQuery = contactState.AutoSuggestState.isAutoSuggestQuery;
    this.suggestionsCount = contactState.AutoSuggestState.autoSuggestResultsV2?.suggestions?.length ?? 0;
    this.queryIndex = contactState.AutoSuggestState.autoSuggestFocusedIndex;
  }
}

// ReInitialize OneDS as needed.
// This should be called when the home/userInfo call succeeds, which means user signs in successfully.
export const reInitializeOneDsAsNeeded = (userInfo: UserInfo, selfHelpStatus: SelfHelpStatus) => {
  if (!isReInitializationEnabled) {
    return;
  }

  const isEu =
    !!userInfo?.Provider &&
    AuthProvider[userInfo.Provider] === AuthProvider.OrgId &&
    // If user's data boundary contains any of the EU data boundary values
    userInfo?.DataBoundary?.some(db => DataBoundaryValues[DataBoundary.EU].indexOf(db) >= 0);
  const previousConfig = currentWindow?.analytics?.config;
  const oneDsEndpointUrl = previousConfig?.endpointUrl;
  const shouldReInitializeForEu = isEu && oneDsEndpointUrl !== OneDSEndpoints[DataBoundary.EU];
  const shouldReInitializeForRow = !isEu && oneDsEndpointUrl !== OneDSEndpoints[DataBoundary.ROW];

  if (!!previousConfig && (shouldReInitializeForRow || shouldReInitializeForEu)) {
    try {
      const localId = currentWindow?.analytics?.getPropertyManager()?.getPropertiesContext()?.user?.getLocalId();
      const config = {
        instrumentationKey: previousConfig?.instrumentationKey,
        // Post channel configuration
        channelConfiguration: previousConfig?.channelConfiguration,
        endpointUrl: shouldReInitializeForEu ? OneDSEndpoints[DataBoundary.EU] : OneDSEndpoints[DataBoundary.ROW],
        propertyConfiguration: previousConfig?.propertyConfiguration,
        // Web Analytics Plugin configuration
        webAnalyticsConfiguration: {
          autoCapture: previousConfig?.webAnalyticsConfiguration?.autoCapture,
          callback: previousConfig?.webAnalyticsConfiguration?.callback,
          coreData: previousConfig?.webAnalyticsConfiguration?.coreData,
          pageTags: previousConfig?.webAnalyticsConfiguration?.pageTags,
          isLoggedIn: true,
          urlCollectHash: previousConfig?.webAnalyticsConfiguration?.urlCollectHash,
          urlCollectQuery: previousConfig?.webAnalyticsConfiguration?.urlCollectQuery,
          useDefaultContentName: previousConfig?.webAnalyticsConfiguration?.useDefaultContentName,
          // TODO vso 4857604(acheng): update property to allow mix mode
          useShortNameForContentBlob: previousConfig?.webAnalyticsConfiguration?.useShortNameForContentBlob,
          syncMuid: previousConfig?.webAnalyticsConfiguration?.syncMuid,
        },
      };

      // If user signs in in the middle of our flow and there is OneDs reinitialization,
      // the logs of the whole impression will be split into different OneDs collectors depend on the endpoint URLs.
      // Here we send event to the previous OneDs endpoint,
      // to indicate that the first half impression "completed" due to OneDs reinitialization.
      // Data pipeline may use this event to ignore this impression,
      // or combine logs from other OneDs collectors by using the data.baseData.properties.pageTags.metaTags.ms.correlationId
      const impressionCompletedBeforeOverrideTags = {
        content: {
          contentName: 'Before',
          formnm: 'maxFunnel',
          areaName: 'OneDsReinitialization',
          fileName: 'oneDsUtil.ts',
          methodName: 'reInitializeOneDsAsNeeded',
          oneDsEndpointUrlBefore: oneDsEndpointUrl,
          oneDsEndpointUrlAfter: config.endpointUrl,
        },
        behavior: 'IMPRESSION', // 280
      };
      captureContentUpdate(impressionCompletedBeforeOverrideTags);

      currentWindow.analytics.unload();
      currentWindow.analytics = new currentWindow.oneDS.ApplicationInsights();
      // This global setting allows UHF to send oneds data
      currentWindow.owap = currentWindow.analytics;
      currentWindow.analytics?.initialize(config, []);
      // Note this change is to bring Cid to localId field in ext.user object.
      // Please contact Magic data engineering team if Auth Plugin is available and you want to pull the user id in the ext.user.authId field.
      currentWindow.analytics.getPropertyManager()?.getPropertiesContext()?.user?.setLocalId(localId);

      // Here we send event to the new OneDs endpoint,
      // to indicate that the second half impression "started" due to OneDs reinitialization.
      // Data pipeline may use this event to ignore this impression,
      // or combine logs from other OneDs collectors by using the data.baseData.properties.pageTags.metaTags.ms.correlationId
      const impressionCompletedAfterOverrideTags = {
        content: {
          contentName: 'After',
          formnm: 'maxFunnel',
          areaName: 'OneDsReinitialization',
          fileName: 'oneDsUtil.ts',
          methodName: 'reInitializeOneDsAsNeeded',
          oneDsEndpointUrlBefore: oneDsEndpointUrl,
          oneDsEndpointUrlAfter: config.endpointUrl,
          selfHelpStatus: selfHelpStatus,
        },
        behavior: 'IMPRESSION', // 280
      };
      captureContentUpdate(impressionCompletedAfterOverrideTags);
    } catch (error) {
      console.log(`reInitializeOneDsAsNeeded(userInfo) error: ${error}.`);
    }
  }
};
